import { ReactNode, useLayoutEffect, useRef } from "react";

// Below this, a title would be unreadable rather than merely tight — better
// to let something truly enormous clip than shrink it to a squint.
const MIN_SCALE = 0.4;

/**
 * A single line of text that shrinks its own font size to stay inside its
 * box, instead of wrapping or forcing the page into horizontal scroll. Made
 * for the big display-font titles (event names, mostly) that read fine at
 * their set size for a short word but would otherwise blow out the layout
 * for a long one.
 *
 * Font size, not `transform: scale`, so the line's box actually shrinks
 * along with it rather than leaving reserved space behind.
 */
export function FitText({ children, className = "" }: { children: ReactNode; className?: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  // The size the className asks for, read off the DOM once so repeated fits
  // always shrink from the same starting point instead of compounding.
  const baseFontSize = useRef<number | null>(null);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const text = textRef.current;
    if (!container || !text) return;

    const fit = () => {
      if (baseFontSize.current === null) {
        baseFontSize.current = parseFloat(getComputedStyle(text).fontSize);
      }
      const base = baseFontSize.current;
      text.style.fontSize = `${base}px`;

      const containerWidth = container.clientWidth;
      const textWidth = text.scrollWidth;
      if (containerWidth === 0 || textWidth <= containerWidth) return;

      // A hair under the exact ratio: sub-pixel rounding between the
      // measured width and the width the browser actually paints at
      // otherwise leaves the last glyph or two poking past the edge and
      // clipped by the container's overflow-hidden.
      const ratio = Math.max(MIN_SCALE, (containerWidth / textWidth) * 0.98);
      text.style.fontSize = `${base * ratio}px`;
    };

    fit();
    // Fonts loading after first paint (the display font in particular) swap
    // in with different metrics than the fallback fit() first measured
    // against, so it needs to run again once the real font is ready.
    document.fonts?.ready?.then(fit);
    // The container's own box rarely changes, but the display font swapping
    // in after first paint (or the title itself changing) is exactly what
    // moves scrollWidth, so both are watched.
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    observer.observe(text);
    return () => observer.disconnect();
  }, [children]);

  return (
    <div ref={containerRef} className="min-w-0 overflow-hidden">
      <div ref={textRef} className={`whitespace-nowrap ${className}`}>
        {children}
      </div>
    </div>
  );
}
